import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  getInseeCodeFromLegalForm,
  LEGAL_FORMS,
} from '@shinetools/legal-form-library';

import asBentoPage from 'common/bento/hoc/asBentoPage';
import Accordion from 'components/_core/Accordion';
import Button from 'components/_core/Button';
import * as Form from 'components/_core/form';
import { INTERCOM_LAUNCHER_PROPS } from 'features/Intercom';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';
import logger from 'helpers/logger';

import Header from '../../../../components/Header';
import CapitalDepositQuestionPage from '../../components/QuestionPage';
import companyLegalInfoSchema, {
  CompanyLegalInfoSchema,
} from '../../libs/companyLegalInfoSchema';
import { CapitalDepositPage } from '../../types';
import locales from './locales';

const LEGAL_FORMS_OPTIONS = [
  {
    label: 'EARL',
    legalForm: LEGAL_FORMS.EARL,
  },
  {
    label: 'GAEC',
    legalForm: LEGAL_FORMS.GAEC,
  },
  {
    label: 'SARL / EURL',
    legalForm: LEGAL_FORMS.SARL,
  },
  {
    label: 'SAS / SASU',
    legalForm: LEGAL_FORMS.SAS,
  },
  {
    label: 'SCI',
    legalForm: LEGAL_FORMS.SCI,
  },
  {
    label: 'SCM',
    legalForm: LEGAL_FORMS.SCM,
  },
  {
    label: 'SNC',
    legalForm: LEGAL_FORMS.SNC,
  },
  {
    label: 'SELARL',
    legalForm: LEGAL_FORMS.SELARL,
  },
];

type RadioOptions = { label: string; value: string }[];

/**
 * Returns a list of radio option from the LEGAL_FORMS_OPTIONS array
 * The value will be the insee code computed from the legal form
 */
const getRadioOptions = (): RadioOptions => {
  const radioOptions: RadioOptions = [];

  LEGAL_FORMS_OPTIONS.forEach(({ label, legalForm }) => {
    const inseeCode = getInseeCodeFromLegalForm(legalForm);

    if (inseeCode === undefined) {
      logger.warn(`No insee code for legal form ${legalForm}`);
    } else {
      radioOptions.push({ label, value: inseeCode });
    }
  });

  return radioOptions;
};
const radioOptions = getRadioOptions();

type CapitalDepositCompanyLegalInfoProps = {
  onSubmit: (values: CompanyLegalInfoSchema) => void;
  formData: Partial<CompanyLegalInfoSchema>;
  isSending: boolean;
  isMandatee: boolean;
} & CapitalDepositPage;

const CapitalDepositCompanyLegalInfo =
  asBentoPage<CapitalDepositCompanyLegalInfoProps>(
    ({
      formData,
      isMandatee,
      isSending,
      Layout,
      onPrev,
      onSubmit,
      progress,
    }) => {
      const {
        control,
        formState: { errors },
        handleSubmit,
        register,
      } = useForm<CompanyLegalInfoSchema>({
        defaultValues: formData,
        mode: 'onBlur',
        resolver: zodResolver(companyLegalInfoSchema),
      });

      const submitHandler = handleSubmit((values) => {
        // handleSubmit is called only if form validation is successful
        onSubmit(values as CompanyLegalInfoSchema);
      });

      return (
        <Layout nav={<OBNavigation onPrev={onPrev} />}>
          <CapitalDepositQuestionPage
            as="form"
            button={
              <Button icon="arrow-right" isLoading={isSending} type="submit">
                {locales.ctaSubmit}
              </Button>
            }
            onSubmit={submitHandler}
            progress={progress}
          >
            <Header
              description={
                isMandatee
                  ? locales.description.mandatee
                  : locales.description.nonMandatee
              }
              title={
                isMandatee ? locales.title.mandatee : locales.title.nonMandatee
              }
            />

            <Form.Field
              error={errors.legalName?.message}
              label={
                isMandatee
                  ? locales.legalName.label.mandatee
                  : locales.legalName.label.nonMandatee
              }
              marginBottom="space-24"
            >
              <Form.Input
                {...register('legalName')}
                placeholder={
                  isMandatee
                    ? locales.legalName.placeholder.mandatee
                    : locales.legalName.placeholder.nonMandatee
                }
              />
            </Form.Field>

            <Form.Field
              error={errors.inseeLegalFormCode?.message}
              label={
                isMandatee
                  ? locales.inseeLegalFormCode.label.mandatee
                  : locales.inseeLegalFormCode.label.nonMandatee
              }
              marginBottom="space-24"
            >
              <Controller
                control={control}
                name="inseeLegalFormCode"
                render={({ field }) => {
                  const selectedOption = radioOptions.find(
                    (option) => option.value === field.value,
                  );

                  return (
                    <Form.SunshineSelect
                      onChange={(option) => field.onChange(option?.value)}
                      options={radioOptions}
                      placeholder={locales.inseeLegalFormCode.placeholder}
                      value={selectedOption}
                    />
                  );
                }}
              />
            </Form.Field>

            <Accordion>
              <Accordion.Item
                title={
                  isMandatee
                    ? locales.helpTitle.mandatee
                    : locales.helpTitle.nonMandatee
                }
              >
                <Accordion.Text>
                  {isMandatee
                    ? locales.helpDescription.mandatee
                    : locales.helpDescription.nonMandatee}
                </Accordion.Text>

                <Accordion.Link {...INTERCOM_LAUNCHER_PROPS}>
                  {locales.helpLink}
                </Accordion.Link>
              </Accordion.Item>
            </Accordion>
          </CapitalDepositQuestionPage>
        </Layout>
      );
    },
  );

export default CapitalDepositCompanyLegalInfo;
